/**
 * SHER-782 follow-up — when the patient page may replace its content with the Loader.
 *
 * The Loader renders no children, so every condition here unmounts the whole patient
 * workspace. That is fine while there is genuinely nothing to show, and harmful the
 * moment a component below owns state that has to survive an in-flight request.
 *
 * SHER-724 already learned this once: gating on a delete-in-flight unmounted the
 * toolbar, and with it the reason modal that had to stay open to explain why the
 * deletion was refused. The update path kept the same mistake through a different
 * flag. `patientsSubmissions.loading` is dispatched by SUBMISSIONS_PATIENT_LOADING at
 * the start of *every* submission save, so a save unmounted FillSurvey mid-request.
 * When the API then refused the edit for a missing reason, FillSurvey's catch set the
 * modal on a component that no longer existed — React warns exactly that — and the
 * remount reset it, so the clinician saw an error and had no way to give the reason.
 *
 * The distinction the flag cannot make, and this function does: "no data yet" versus
 * "a mutation is in flight over data already on screen". Only the first is a Loader.
 */

export interface PatientLoaderInputs {
    investigationsLoading: boolean;
    profileLoading: boolean;
    loadingDepartments: boolean;
    /** null until the submissions for this patient have been resolved at least once. */
    surveyRecords: unknown[] | null;
}

export function shouldRenderPatientLoader(inputs: PatientLoaderInputs): boolean {
    const { investigationsLoading, profileLoading, loadingDepartments, surveyRecords } = inputs;
    return Boolean(investigationsLoading || profileLoading || loadingDepartments) || surveyRecords === null;
}
